/**
 * Data I/O executors — read_range, write_range, set_cell_formula,
 * clear_range, find_replace.
 *
 * Each export is a CoreFn: (ctx, args?) => Promise<unknown> | unknown
 * that queues operations on proxy objects. The outer `executeExcelTool`
 * wrapper calls `context.sync()` after the core function returns unless
 * the core function does its own intermediate syncs.
 *
 * See docs/excel-client-side-tools/phase-3-client-tool-executors.md
 */

/* global Excel */

import { parseRangeExtent, resizedAddress } from "../a1";
import {
  CHUNK_SIZE,
  qualify,
  type ResolvedRange,
  resolveExcelEnum,
  resolveRange,
} from "./utils";

// ---------------------------------------------------------------------------
// Shape checks
// ---------------------------------------------------------------------------

/**
 * Refuse a write whose data doesn't fill the multi-cell range it names.
 * Anchoring at the top-left and writing whatever arrived silently leaves the
 * rest of the named range holding stale data while reporting success.
 */
function assertShapeMatchesRange(
  toolName: string,
  rangeAddr: string,
  target: ResolvedRange,
  rows: number,
  cols: number,
  what: string
): void {
  const extent = parseRangeExtent(target.address);
  if (!extent || (extent.rows === rows && extent.cols === cols)) {
    return;
  }
  const anchor = target.address.split(":")[0];
  throw new Error(
    `${toolName}: range "${rangeAddr}" spans ${extent.rows}×${extent.cols} but ` +
      `${what} are ${rows}×${cols}. Nothing was written. Pass a single-cell ` +
      `anchor (e.g. "${anchor}") to size the write from the data, or supply ` +
      `${what} that exactly fill the range.`
  );
}

/**
 * The sheet-qualified address a `rows`×`cols` write from `target` covers.
 * A named-range anchor can't be resolved without a sync, so it is reported
 * as given (the tool descriptions say so).
 */
function writtenAddress(
  target: ResolvedRange,
  rows: number,
  cols: number
): string {
  const resized = resizedAddress(target.address, rows, cols);
  return resized ? qualify(target.sheetName, resized) : target.qualifiedAddress;
}

// ---------------------------------------------------------------------------
// write_range
// ---------------------------------------------------------------------------

/**
 * Write a 2-D values array into a worksheet range.
 *
 * Expected args:
 *   range:      string     — Range address, e.g. "A1", "A1:C3", "Sheet1!B2:D10"
 *   values:     unknown[][] — 2-D array of values to write
 *   sheetName?: string     — Worksheet name (defaults to active sheet)
 *
 * A single-cell address (e.g. "A1") is auto-expanded to the values shape. A
 * multi-cell address must match the values shape exactly.
 */
export async function writeRangeCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const values = args?.values as unknown[][] | undefined;
  const sheetName = args?.sheetName as string | undefined;

  if (!rangeAddr) {
    throw new Error(
      "write_range requires a `range` argument (e.g. 'A1' or 'B2:D10')."
    );
  }
  if (!values || !Array.isArray(values) || values.length === 0) {
    throw new Error("write_range requires a `values` argument (2-D array).");
  }

  // Ensure every row is an array (defensive — the AI should send 2-D)
  const normalised: unknown[][] = values.map((row) =>
    Array.isArray(row) ? row : [row]
  );

  const rows = normalised.length;
  const cols = Math.max(...normalised.map((r) => r.length));

  const target = resolveRange(ctx, rangeAddr, sheetName);
  assertShapeMatchesRange(
    "write_range",
    rangeAddr,
    target,
    rows,
    cols,
    "values"
  );

  // Pad ragged rows with empty strings so Office.js doesn't throw
  for (const row of normalised) {
    while (row.length < cols) {
      row.push("");
    }
  }

  // Always anchor from the top-left cell of the given range, then resize
  // to match the values shape. `getResizedRange(deltaRows, deltaCols)` adds
  // deltas to the current 1×1 cell, so (rows-1, cols-1) yields the exact
  // dimensions we need.
  const anchorCell = target.range.getCell(0, 0);
  const targetRange = anchorCell.getResizedRange(rows - 1, cols - 1);

  // For large writes, chunk to stay within the ~5 MB sync payload limit
  if (rows > CHUNK_SIZE) {
    for (let start = 0; start < rows; start += CHUNK_SIZE) {
      const end = Math.min(start + CHUNK_SIZE, rows);
      const chunk = normalised.slice(start, end);
      const chunkRange = anchorCell
        .getOffsetRange(start, 0)
        .getResizedRange(chunk.length - 1, cols - 1);
      chunkRange.values = chunk;
      await ctx.sync();
    }
  } else {
    targetRange.values = normalised;
    // The outer wrapper calls context.sync()
  }

  return {
    success: true,
    range: writtenAddress(target, rows, cols),
    rowsWritten: rows,
    colsWritten: cols,
  };
}

// ---------------------------------------------------------------------------
// read_range
// ---------------------------------------------------------------------------

/**
 * Read values (and optionally formulas / display text) from a worksheet range.
 *
 * Expected args:
 *   range:      string  — Range address, e.g. "A1:D10" or "Sheet2!A1:D10"
 *   sheetName?: string  — Worksheet name (defaults to active sheet)
 *   formulas?:  boolean — If true, also return formulas
 *   text?:      boolean — If true, also return display text (formatted dates,
 *                         currency, percentages as shown in the grid)
 *
 * For large ranges (>CHUNK_SIZE rows), reads are chunked to stay under
 * the ~5 MB `context.sync()` payload limit.
 */
export async function readRangeCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const includeFormulas = args?.formulas === true;
  const includeText = args?.text === true;

  if (!rangeAddr) {
    throw new Error(
      "Reading a range requires a `range` argument (e.g. 'A1:D10')."
    );
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  range.load("address,rowCount,columnCount");
  await ctx.sync();

  const loadExtras = (target: Excel.Range) => {
    target.load("values");
    if (includeFormulas) {
      target.load("formulas");
    }
    if (includeText) {
      target.load("text");
    }
  };

  // Small range — load everything in one sync (original fast path)
  if (range.rowCount <= CHUNK_SIZE) {
    loadExtras(range);
    await ctx.sync();

    const result: Record<string, unknown> = {
      address: range.address,
      values: range.values,
      rowCount: range.rowCount,
      columnCount: range.columnCount,
    };
    if (includeFormulas) {
      result.formulas = range.formulas;
    }
    if (includeText) {
      result.text = range.text;
    }
    return result;
  }

  // Large range — chunk reads to stay under ~5 MB payload limit
  const allValues: unknown[][] = [];
  const allFormulas: unknown[][] = [];
  const allText: string[][] = [];

  for (let start = 0; start < range.rowCount; start += CHUNK_SIZE) {
    const size = Math.min(CHUNK_SIZE, range.rowCount - start);
    const chunk = range
      .getRow(start)
      .getResizedRange(size - 1, range.columnCount - 1);
    loadExtras(chunk);
    await ctx.sync();
    allValues.push(...chunk.values);
    if (includeFormulas) {
      allFormulas.push(...chunk.formulas);
    }
    if (includeText) {
      allText.push(...chunk.text);
    }
  }

  const result: Record<string, unknown> = {
    address: range.address,
    values: allValues,
    rowCount: range.rowCount,
    columnCount: range.columnCount,
  };
  if (includeFormulas) {
    result.formulas = allFormulas;
  }
  if (includeText) {
    result.text = allText;
  }
  return result;
}

// ---------------------------------------------------------------------------
// set_cell_formula
// ---------------------------------------------------------------------------

/**
 * Set formulas on a range of cells.
 *
 * Expected args:
 *   range:      string     — Cell/range address, e.g. "E2" or "B1:B5"
 *   formulas:   string[][] — 2-D array of formula strings, e.g. [["=SUM(A1:A10)"]]
 *   sheetName?: string     — Worksheet name
 *
 * Like `write_range`, a single-cell address is sized from the formulas array
 * and a multi-cell address must match it exactly.
 */
export function setCellFormulaCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const formulas = args?.formulas as string[][] | undefined;
  const sheetName = args?.sheetName as string | undefined;

  if (!rangeAddr) {
    throw new Error("set_cell_formula requires a `range` argument.");
  }
  if (!formulas || !Array.isArray(formulas) || formulas.length === 0) {
    throw new Error(
      "set_cell_formula requires a `formulas` argument (2-D array of formula strings)."
    );
  }

  const rows = formulas.length;
  const cols = Math.max(
    ...formulas.map((r) => (Array.isArray(r) ? r.length : 1))
  );
  if (formulas.some((r) => !Array.isArray(r) || r.length !== cols)) {
    throw new Error(
      "set_cell_formula: every row of `formulas` must have the same number of " +
        'cells. Use "" for a cell that should stay empty.'
    );
  }

  const target = resolveRange(ctx, rangeAddr, sheetName);
  assertShapeMatchesRange(
    "set_cell_formula",
    rangeAddr,
    target,
    rows,
    cols,
    "formulas"
  );

  target.range.getCell(0, 0).getResizedRange(rows - 1, cols - 1).formulas =
    formulas;

  return {
    success: true,
    range: writtenAddress(target, rows, cols),
    formulaCount: rows * cols,
  };
}

// ---------------------------------------------------------------------------
// clear_range
// ---------------------------------------------------------------------------

/**
 * Clear contents, formats, or all from a range.
 *
 * Expected args:
 *   range:      string — Range address
 *   applyTo?:   "contents" | "formats" | "all" (default: "all")
 *   sheetName?: string — Worksheet name
 */
export function clearRangeCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const applyToArg = (args?.applyTo as string) ?? "all";
  const sheetName = args?.sheetName as string | undefined;

  if (!rangeAddr) {
    throw new Error("clear_range requires a `range` argument.");
  }

  const target = resolveRange(ctx, rangeAddr, sheetName);

  const applyTo = resolveExcelEnum(Excel.ClearApplyTo, applyToArg, "applyTo");
  target.range.clear(applyTo);

  return { success: true, range: target.qualifiedAddress, cleared: applyToArg };
}
